import { Button, Icon, Input, Loading, Typography } from '@smartgift/frontend-toolkit';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  UploadEmailSpreadSheetModal,
  ContactBGSModal,
  ContactsEditModal,
  WithoutContactReviewAndSubmitModal,
  UploadedEmailModal
} from '@hero/components/organisms';
import {
  CONTACTLESS_VERIFICATION_TYPES,
  EDITABLE_VERIFICATION_LIVE,
  NON_EDITABLE_RECOGNITION_STATUS,
  PAYMENT_METHOD_TYPES,
  MAX_ALLOWED_CONTACT_COUNT_FCC,
  RECOGNITIONS_TAB,
  CONTACT_TYPE
} from '@hero/constants';
import { useMaxContactHasError, useSendRemindersNotification } from '@hero/hooks';
import { DetailItem } from '@hero/molecules';
import { useGetMerchant } from '@hero/services/merchant-service/hooks';
import {
  useGetRecognition,
  useGetRecognitionRecipientList,
  useRecogCalculateEstimatedCharge,
  useRecogRecipientsBulkUpsert,
  useSendRemindersRecognition,
  useUpdateRecognition
} from '@hero/services/order-service';

import { AddDomainModal } from '../../../SendWithoutContactCard';

import * as S from './style';

const recipientVerificationTranslationMapping = {
  [CONTACTLESS_VERIFICATION_TYPES.PHONE]: 'phoneVerification',
  [CONTACTLESS_VERIFICATION_TYPES.EMAIL_DOMAIN]: 'authorizeDomain',
  [CONTACTLESS_VERIFICATION_TYPES.NONE]: 'noVerificationRequiredLive'
};

// Its used by both flow.
// with contact
// without contact
export const RecipientsAccordion = ({ setActiveTab }) => {
  const { recognitionCode } = useParams();
  const { t } = useTranslation();
  const { data: recognition } = useGetRecognition({ recognitionCode });
  const { mutateAsync: updateRecognition } = useUpdateRecognition();
  const { data: merchantData } = useGetMerchant({ options: { includeConfigs: true } });
  const { data: recognitionRecipients } = useGetRecognitionRecipientList(
    {
      code: recognition?.code,
      options: {
        select: 'contact.id',
        pagination: false
      }
    },
    {
      enabled: !!recognition?.code
    }
  );
  const { mutateAsync: recipientBulkUpsert, isLoading: isRecipientBulkUpsertLoading } =
    useRecogRecipientsBulkUpsert();
  const { mutateAsync: calculateRecognitionCharge, isLoading: isLoadingCalculation } =
    useRecogCalculateEstimatedCharge();

  const [contacts, setContacts] = useState({});
  const [contactBGSModal, setContactBGSModal] = useState(false);
  const [isContactsEditModalOpen, setIsContactsEditModalOpen] = useState(false);
  const [isWithoutContactReviewAndSubmitModal, setIsWithoutContactReviewAndSubmitModal] =
    useState(false);
  const [isAddEmailModal, setIsAddEmailModal] = useState(false);
  const [isAddDomainModal, setIsAddDomainModal] = useState(false);
  const [uploadedEmailModal, setUploadedEmailModal] = useState(false);
  const [newEstimatedCharge, setNewEstimatedCharge] = useState({});

  const recipientCount = recognition?.numberOfContacts;
  const isCCPayment = recognition?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard;

  const maxContactHasError = useMaxContactHasError({
    recognitionCode: recognition?.code,
    contacts,
    recognition
  });

  const isEditableVerificationType = useMemo(() => {
    return EDITABLE_VERIFICATION_LIVE.includes(recognition?.settings?.recipientVerification?.type);
  }, [recognition?.settings?.recipientVerification?.type]);

  const isDetailItemEditable = React.useMemo(
    () => !NON_EDITABLE_RECOGNITION_STATUS.includes(recognition?.status?.current),
    [recognition]
  );

  const recognitionRecipientContactIds = useMemo(() => {
    return (
      recognitionRecipients?.docs.map(doc => doc?.contact?.id).filter(contactId => contactId) || []
    );
  }, [recognitionRecipients]);

  async function onUpdateRecipient() {
    const maxAllowedContactFCC = merchantData.configs[MAX_ALLOWED_CONTACT_COUNT_FCC];

    if (
      maxAllowedContactFCC < contacts.maxCount &&
      recognition.paymentMethod.type !== PAYMENT_METHOD_TYPES.houseAccount
    ) {
      return setContactBGSModal(true); // now allowed for now
    }

    await updateRecognition({
      params: { recognitionCode: recognition?.code },
      body: {
        contacts
      },
      successMessage: t('recipientsUpdatedSuccessfully')
    });

    setIsWithoutContactReviewAndSubmitModal(false);
  }

  async function updateContacts(contactIds) {
    await recipientBulkUpsert({
      params: {
        code: recognition.code
      },
      contactIds,
      recipientContactIds: recognitionRecipientContactIds
    });

    await updateRecognition({
      params: { recognitionCode },
      body: {
        contacts: {
          type: 'list'
        }
      },
      successMessage: t('recipientsUpdatedSuccessfully')
    });
  }

  const { send } = useSendRemindersNotification({ useSendReminder: useSendRemindersRecognition });

  useEffect(() => {
    setContacts(recognition?.contacts || {});
  }, [recognition?.contacts]);

  const isReminderActive = useMemo(() => {
    const totalRecipients = recognition?.acceptanceRates?.total;
    const totalAcceptedRecipients = recognition?.acceptanceRates?.accepted;

    if (totalRecipients === 0) return false;
    if (totalAcceptedRecipients === 0) return true;

    return totalRecipients - totalAcceptedRecipients > 0;
  }, [recognition]);

  return (
    <S.Content>
      <S.RecipientHeader>
        {contacts?.type === 'list' && isDetailItemEditable && isReminderActive && (
          <>
            <Button
              startIcon={<Icon name="email" />}
              onClick={() => send(recognition?.code)}
              variant="link"
            >
              {t('remindRecipients')}
            </Button>
            <S.VerticalDivider />
          </>
        )}
        <Button
          startIcon={<Icon name="preview" />}
          onClick={() => window.open(recognition?.link?.preview, '_blank')}
          target="_blank"
          variant="text"
          size="sm"
        >
          {t('viewGiftPreview')}
        </Button>
        <S.VerticalDivider />
        <Button
          startIcon={<Icon name="contacts" />}
          onClick={() => setActiveTab(RECOGNITIONS_TAB.recipients)}
          variant="text"
          size="sm"
        >
          {t('viewRecipientDetails')}
        </Button>
      </S.RecipientHeader>
      <S.RecipientBody>
        <DetailItem
          editable={isDetailItemEditable && recognition?.source === 'hero_dashboard'}
          editButtonText={t(contacts?.type === 'list' ? 'addRecipients' : 'editRecipientLimit')}
          isCTADisabled={maxContactHasError}
          title={`${t('recipients')}: `}
          titleStyle={{
            width: '100px'
          }}
          bodyStyle={{ alignItems: 'center' }}
          closeWithoutSave={() => {
            setContacts(recognition?.contacts);
          }}
          saveDetail={async () => {
            if (recipientCount !== contacts.maxCount) {
              const calculated = await calculateRecognitionCharge({
                params: {
                  code: recognitionCode
                },
                body: {
                  products: recognition?.products,
                  recipientCount: contacts?.maxCount
                }
              });

              if (calculated.difference.total > 0) {
                setNewEstimatedCharge(calculated);
                setIsWithoutContactReviewAndSubmitModal(true);
              } else {
                onUpdateRecipient();
              }
            }
          }}
          onClickEditButton={
            contacts?.type === CONTACT_TYPE.list &&
            (() => {
              setIsContactsEditModalOpen(true);
            })
          }
          editButton={
            contacts?.type === CONTACT_TYPE.list &&
            (() => {
              return (
                <>
                  <Icon name="add" />
                  {t('addRecipients')}
                </>
              );
            })
          }
          editInput={() => (
            <Input
              error={maxContactHasError}
              value={contacts?.maxCount?.toString() || ''}
              type="number"
              label={`${t('recipientQuantity')}*`}
              variant="outlined"
              onKeyPress={event => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              onChange={event => {
                setContacts({
                  ...contacts,
                  maxCount: event.target.value
                });
              }}
            />
          )}
        >
          {recipientCount}
        </DetailItem>
      </S.RecipientBody>

      {recognition && isContactsEditModalOpen && (
        <ContactsEditModal
          onNextReviewAndConfirmStep={({ onNext, numberOfContacts }) => {
            const maxAllowedContactFCC = merchantData.configs[MAX_ALLOWED_CONTACT_COUNT_FCC];
            if (
              maxAllowedContactFCC < numberOfContacts &&
              recognition.paymentMethod.type !== PAYMENT_METHOD_TYPES.houseAccount
            ) {
              return setContactBGSModal(true); // now allowed for now
            } else {
              onNext();
            }
          }}
          isModalOpen={isContactsEditModalOpen}
          onRequestClose={() => setIsContactsEditModalOpen(false)}
          updateContacts={updateContacts}
          selectedContactIds={recognitionRecipientContactIds || []}
          isLiveFeature
          isCCPayment={isCCPayment}
        />
      )}

      {isWithoutContactReviewAndSubmitModal && (
        <WithoutContactReviewAndSubmitModal
          newContactCount={contacts?.maxCount}
          contactCount={recognition?.contacts?.maxCount}
          isOpen={isWithoutContactReviewAndSubmitModal}
          onRequestClose={() => setIsWithoutContactReviewAndSubmitModal(false)}
          onSave={onUpdateRecipient}
          newEstimatedCharge={newEstimatedCharge}
          isCCPayment={isCCPayment}
        />
      )}

      {contacts?.type !== 'list' && recognition?.settings?.recipientVerification?.type && (
        <S.RecipientVerificationDetails>
          <DetailItem
            editable={isEditableVerificationType}
            title={`${t('recipientVerification')}:`}
            editButton={() => {
              return (
                <Button
                  style={{ padding: 0 }}
                  startIcon={<Icon name="add" px="13px" />}
                  variant="link"
                  onClick={() => {
                    setIsAddDomainModal(true);
                  }}
                >
                  {t('addDomain')}
                </Button>
              );
            }}
          >
            {t(
              recipientVerificationTranslationMapping[
                recognition?.settings?.recipientVerification?.type
              ]
            )}
          </DetailItem>

          {recognition?.settings?.recipientVerification?.type ===
            CONTACTLESS_VERIFICATION_TYPES.EMAIL_DOMAIN && (
            <DetailItem title={`${t('authorizedDomains')}: `} editButtonText={t('addDomain')}>
              {recognition?.settings?.recipientVerification?.params?.emailDomains?.map(
                emailDomain => (
                  <Typography key={emailDomain} as="p" variant="body3">
                    {emailDomain}
                  </Typography>
                )
              )}
            </DetailItem>
          )}

          <UploadedEmailModal
            recognitionCode={recognition?.code}
            recognitionMaxCount={recognition?.contacts?.maxCount}
            openModal={uploadedEmailModal}
            onRequestClose={setUploadedEmailModal}
            onStartOver={() => {
              setUploadedEmailModal(false);
              setIsAddEmailModal(true);
            }}
          />

          <UploadEmailSpreadSheetModal
            openModal={isAddEmailModal}
            onRequestClose={() => setIsAddEmailModal(false)}
            isPrimaryButtonDisabled={isRecipientBulkUpsertLoading}
            onEmailUpdateDone={async emails => {
              await recipientBulkUpsert({
                params: {
                  code: recognition.code
                },
                body: {
                  recipients: emails.map(email => ({
                    email: email.email
                  }))
                }
              });
            }}
          />

          <AddDomainModal
            previousAuthorizedDomains={
              recognition?.settings?.recipientVerification?.params?.emailDomains
            }
            onSave={async domains => {
              await updateRecognition({
                params: { recognitionCode: recognition.code },
                body: {
                  settings: {
                    recipientVerification: {
                      type: CONTACTLESS_VERIFICATION_TYPES.EMAIL_DOMAIN,
                      params: {
                        emailDomains: [
                          ...(recognition?.settings?.recipientVerification?.params?.emailDomains ||
                            []),
                          ...domains
                        ]
                      }
                    }
                  }
                }
              });
              setIsAddDomainModal(false);
            }}
            openModal={isAddDomainModal}
            onRequestClose={() => {
              setIsAddDomainModal(false);
            }}
          />
        </S.RecipientVerificationDetails>
      )}

      {isLoadingCalculation && (
        <S.LoadingWrapper>
          <Loading type="ring" color="var(--color-accent-primary-default)" />
        </S.LoadingWrapper>
      )}
      {contactBGSModal && (
        <ContactBGSModal
          recognitionCode={recognition?.code}
          isModalOpen={contactBGSModal}
          onModalOpen={() => {
            setContactBGSModal(false);
          }}
        />
      )}
    </S.Content>
  );
};
